import { graphql } from 'react-relay';

import type { Option } from '@atlaskit/editor-common/extensions/field-definitions';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { escapeText } from '../cql/transformers.ts';

import type {
	usernameResolverQuery$data,
	usernameResolverQuery$variables,
} from './__generated__/usernameResolverQuery.graphql.ts';

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- file name contains hyphens, incompatible with GraphQL naming
const usernameResolverQuery = graphql`
	query usernameResolverQuery($cql: String!) {
		searchUser: confluence_searchUser(cql: $cql, first: 25, sitePermissionTypeFilter: "all") {
			edges {
				node {
					user: confluencePerson {
						displayName
						accountId
						profilePicture {
							path
						}
					}
				}
			}
		}
	}
`;

const getQueryTerm = (term: string) => {
	// query term looks like this: "type = user AND (user.fullname ~ \"bob*\" OR user = \"bob\")"
	return `type = user AND (user.fullname ~ "${term}*" OR user = "${term}")`;
};

const search = async (cql: string) => {
	const result = await queryInterop<usernameResolverQuery$data, usernameResolverQuery$variables>({
		query: usernameResolverQuery,
		variables: {
			cql,
		},
	});

	return (result?.data?.searchUser?.edges ?? []).map((edge) => ({
		label: edge?.node?.user?.displayName ?? '',
		value: edge?.node?.user?.accountId ?? '',
		icon: edge?.node?.user?.profilePicture?.path ?? '',
	}));
};

export const usernameResolver = async (
	searchTerm?: string,
	selectedValue?: string | string[],
): Promise<Option[]> => {
	const searchers: Array<Promise<Option[]>> = [];

	if (searchTerm) {
		searchers.push(search(getQueryTerm(searchTerm)));
	}

	if (selectedValue) {
		if (Array.isArray(selectedValue)) {
			// if values are in array, concat the items into one search phrase
			// e.g if array is ["sponge", "bob"], query term is: "user IN ( \"sponge\", \"bob\")"
			searchers.push(
				search(`user IN (${selectedValue.map((val) => `"${escapeText(val)}"`).join(', ')})`),
			);
		} else {
			// if not an array just pass in the term as selected val
			searchers.push(search(getQueryTerm(selectedValue)));
		}
	}

	return (await Promise.all(searchers)).flat();
};
